<template>
  <div class="wrapper w-100">
    <survey-question-ranked-choice v-if="preview && !isNew" :question="value">
    </survey-question-ranked-choice>
    <div class="clickLayer text-right rounded" @click.stop="edit">
      <div
        v-if="preview && !isNew"
        class="mt-3 mr-2 primary rounded elevation-3 overflow-hidden d-inline-block"
      >
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="white--text"
              tile
              icon
              @click.stop="edit"
            >
              <v-icon small>$edit</v-icon>
            </v-btn>
          </template>
          <span>Edit Question</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="white--text"
              tile
              icon
              @click.stop="$emit('delete')"
            >
              <v-icon small>$delete</v-icon>
            </v-btn>
          </template>
          <span>Delete Question</span>
        </v-tooltip>
      </div>
      <div
        class="mt-3 mr-3 primary rounded elevation-3 overflow-hidden d-inline-block"
      >
        <v-tooltip top v-if="questionIndex !== 0">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="white--text"
              tile
              icon
              @click.stop="$emit('move', -1)"
            >
              <v-icon small>$sortUp</v-icon>
            </v-btn>
          </template>
          <span>Move Up</span>
        </v-tooltip>
        <v-tooltip top v-if="questionIndex !== page.questions.length - 1">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="white--text"
              tile
              icon
              @click.stop="$emit('move', 1)"
            >
              <v-icon small>$sortDown</v-icon>
            </v-btn>
          </template>
          <span>Move Down</span>
        </v-tooltip>
      </div>
    </div>

    <v-dialog
      v-model="openEditor"
      scrollable
      fullscreen
      hide-overlay
      persistent
      no-click-animation
      transition="dialog-bottom-transition"
    >
      <ValidationObserver ref="observer" tag="form" @submit.prevent="onSubmit">
        <v-card rounded="0">
          <div class="elevation-3 primary ">
            <v-container class="pa-0">
              <dialog-title
                :title="`${value.key === '' ? 'Add' : 'Edit'} Question`"
                class="white--text pl-md-0"
                @close="onCancel"
              >
                <template v-slot:actions="{ close }">
                  <v-btn dark icon @click="close">
                    <v-icon>$close</v-icon></v-btn
                  >
                </template>
              </dialog-title>
            </v-container>
          </div>
          <v-card-text ref="body">
            <v-container>
              <v-row>
                <v-col cols="12">
                  Fill out the form below to change the question.
                </v-col>
                <v-col cols="12">
                  <survey-editor-page-select
                    v-model="innerPageKey"
                    :pages="pages"
                    :locked="locked"
                  ></survey-editor-page-select>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <ValidationProvider
                    :rules="{
                      required: true,
                      min: 2,
                      max: 100,
                      notTaken: questionKeys.filter((x) => x !== value.key),
                    }"
                    v-slot="{ errors }"
                    vid="key"
                  >
                    <v-text-field
                      label="Key*"
                      v-model="innerValue.key"
                      outlined
                      :error-messages="errors"
                      persistent-hint
                      hint="Cannot be changed once added."
                      :disabled="!isNew || locked"
                      :autofocus="isNew"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <ValidationProvider
                    :rules="{
                      required: true,
                      min: 2,
                      max: 100,
                      notTaken: questionMappingKeys.filter(
                        (x) => x !== value.mappingKey
                      ),
                    }"
                    v-slot="{ errors }"
                    vid="mappingKey"
                  >
                    <v-text-field
                      label="Mapping Key*"
                      v-model="innerValue.mappingKey"
                      outlined
                      :error-messages="errors"
                      persistent-hint
                      hint=""
                      :disabled="locked"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <ValidationProvider
                    rules="required|min:2|max:500"
                    v-slot="{ errors }"
                    vid="Type"
                  >
                    <v-select
                      label="Type*"
                      v-model="innerValue.type"
                      :items="types"
                      outlined
                      :error-messages="errors"
                      disabled
                      rows="3"
                    ></v-select>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <ValidationProvider
                    :rules="{
                      required: true,
                      min_value: 2,
                      max_value: innerValue.options.length,
                      numeric: true,
                    }"
                    v-slot="{ errors }"
                    vid="slots"
                  >
                    <v-text-field
                      label="Ranking Slots*"
                      v-model="slots"
                      type="number"
                      step="1"
                      outlined
                      :error-messages="errors"
                      :disabled="locked"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" xl="1" lg="2" md="3">
                  <ValidationProvider
                    rules="max:200"
                    v-slot="{ errors }"
                    vid="Text"
                  >
                    <v-text-field
                      label="Number"
                      v-model="innerValue.questionNumber"
                      outlined
                      :error-messages="errors"
                      :disabled="locked"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" xl="11" lg="10" md="9">
                  <ValidationProvider
                    rules="required|min:2|max:2000"
                    v-slot="{ errors }"
                    vid="Text"
                  >
                    <v-textarea
                      label="Question*"
                      v-model="innerValue.text"
                      outlined
                      :error-messages="errors"
                      rows="3"
                      :disabled="locked"
                    ></v-textarea>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12">
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="ResponseCountTarget"
                  >
                    <v-text-field
                      type="number"
                      v-model="innerValue.responseCountTarget"
                      label="Response Goal"
                      hint="Used to calculate a question's response rate goals. Only needed if the goal is different than the base survey response goal."
                      persistent-hint
                      outlined
                      :error-messages="errors"
                      :disabled="loading || archived || optionGoals"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12">
                  <div class="d-flex align-center">
                    <v-btn
                      icon
                      :color="required ? 'secondary' : ''"
                      @click="required = !required"
                      :disabled="locked"
                    >
                      <v-icon>
                        {{ required ? "$checked" : "$unchecked" }}
                      </v-icon>
                    </v-btn>
                    <div @click="required = !required" class="pointer">
                      Required
                    </div>
                  </div>
                  <div class="d-flex align-center">
                    <v-btn
                      icon
                      :color="required ? 'secondary' : ''"
                      @click="isPrivate = !isPrivate"
                      :disabled="locked"
                    >
                      <v-icon>
                        {{ isPrivate ? "$checked" : "$unchecked" }}
                      </v-icon>
                    </v-btn>
                    <div @click="isPrivate = !isPrivate" class="pointer">
                      Hide on Public Reporting
                    </div>
                  </div>
                  <div class="d-flex align-center">
                    <v-btn
                      icon
                      :color="optionGoals ? 'secondary' : ''"
                      @click="optionGoals = !optionGoals"
                      :disabled="locked"
                    >
                      <v-icon>
                        {{ optionGoals ? "$checked" : "$unchecked" }}
                      </v-icon>
                    </v-btn>
                    <div>Option Response Goals</div>
                  </div>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-divider class="mb-3"></v-divider>
                  <div class="d-flex align-center">
                    <div class="flex-fill">
                      <div class="text-h6">Options</div>
                      <p class="text-body-2">
                        Use the grid below to add, edit, or remove options in
                        the question.
                      </p>
                    </div>
                    <v-tooltip left>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          v-bind="attrs"
                          v-on="on"
                          fab
                          color="primary"
                          small
                          elevation="0"
                          @click.stop="
                            () => {
                              cancelQuestionOption();
                              addQuestionOption();
                            }
                          "
                          :disabled="
                            locked ||
                              addingOption ||
                              editingOption > -1 ||
                              (innerValue.options &&
                                innerValue.options.length === 0)
                          "
                          ><font-awesome-icon icon="plus"></font-awesome-icon
                        ></v-btn>
                      </template>
                      <span>Add Option</span>
                    </v-tooltip>
                  </div>
                  <table class="stripped">
                    <tr>
                      <td
                        class="py-2 w-100 text-left grey darken-3 white--text"
                      >
                        Text
                      </td>
                      <td
                        class="py-2 text-left text-left grey darken-3 white--text"
                      >
                        Value
                      </td>
                      <td
                        v-if="optionGoals"
                        class="py-2 text-center text-left grey darken-3 white--text"
                      >
                        Response Goal
                      </td>
                      <td
                        class="py-2 text-center text-left grey darken-3 white--text"
                      >
                        Actions
                      </td>
                    </tr>
                    <template v-for="(option, index) in innerValue.options">
                      <tr
                        :key="`read_${index}`"
                        v-if="
                          editingOption !== index && deletingOption !== index
                        "
                        @click="
                          () => {
                            if (
                              editingOption == -1 &&
                              !addingOption &&
                              !locked
                            ) {
                              editQuestionOption(option, index);
                            }
                          }
                        "
                        :class="{
                          'hover pointer':
                            editingOption == -1 && !addingOption && !locked,
                          'grey--text': editingOption > -1 || addingOption,
                        }"
                      >
                        <td class="py-3 px-4">
                          {{ option.text }}
                        </td>
                        <td class="py-3 px-4">
                          {{ option.value }}
                        </td>
                        <td v-if="optionGoals" class="py-3 px-4">
                          {{ option.responseCountTarget }}
                        </td>
                        <td class="text-no-wrap py-3 px-4">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                @click.stop="
                                  () => {
                                    cancelAddQuestionOption();
                                    editQuestionOption(option, index);
                                  }
                                "
                                :disabled="
                                  editingOption > -1 || addingOption || locked
                                "
                              >
                                <v-icon small>$edit</v-icon>
                              </v-btn>
                            </template>
                            <span>Edit</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                @click.stop="
                                  deleteQuestionOption(option, index)
                                "
                                :disabled="
                                  editingOption > -1 || addingOption || locked
                                "
                              >
                                <v-icon small>$delete</v-icon>
                              </v-btn>
                            </template>
                            <span>Delete</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                :disabled="
                                  locked ||
                                    index === 0 ||
                                    editingOption > -1 ||
                                    addingOption
                                "
                                @click.stop="moveOption(index, -1)"
                              >
                                <v-icon small>$sortUp</v-icon>
                              </v-btn>
                            </template>
                            <span>Move Up</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                :disabled="
                                  locked ||
                                    index + 1 == innerValue.options.length ||
                                    editingOption > -1 ||
                                    addingOption
                                "
                                @click.stop="moveOption(index, 1)"
                              >
                                <v-icon small>$sortDown</v-icon>
                              </v-btn>
                            </template>
                            <span>Move Down</span>
                          </v-tooltip>
                        </td>
                      </tr>
                      <ValidationObserver
                        v-slot="{ invalid }"
                        :key="`edit_${index}`"
                        v-if="editingOption === index && !locked"
                        tag="tr"
                        ref="editOptionObserver"
                      >
                        <td class="px-4 py-4" style="vertical-align: top">
                          <ValidationProvider
                            rules="required|min:2|max:2000"
                            v-slot="{ errors }"
                            vid="questionOptionText"
                          >
                            <v-text-field
                              label="Text*"
                              v-model="editOption.text"
                              outlined
                              :error-messages="errors"
                              :hide-details="errors.length == 0"
                              @keydown.enter.prevent="confirmQuestionOption"
                              @keydown.esc="cancelQuestionOption"
                              autofocus
                            ></v-text-field>
                          </ValidationProvider>
                        </td>
                        <td class="px-4 py-4" style="vertical-align: top">
                          <ValidationProvider
                            rules="required|max:100"
                            v-slot="{ errors }"
                            vid="questionOptionValue"
                          >
                            <v-text-field
                              label="Value*"
                              v-model="editOption.value"
                              outlined
                              :error-messages="errors"
                              :hide-details="errors.length == 0"
                              @keydown.enter.prevent="confirmQuestionOption"
                              @keydown.esc="cancelQuestionOption"
                              style="width: 150px"
                              disabled
                            ></v-text-field>
                          </ValidationProvider>
                        </td>
                        <td
                          v-if="optionGoals"
                          class="px-4 py-4"
                          style="vertical-align: top"
                        >
                          <ValidationProvider
                            rules="required"
                            v-slot="{ errors }"
                            vid="questionOptionResponseCountTarget"
                          >
                            <v-text-field
                              type="number"
                              label="Response Goal*"
                              v-model="editOption.responseCountTarget"
                              outlined
                              :error-messages="errors"
                              :hide-details="errors.length == 0"
                              @keydown.enter.prevent="confirmQuestionOption"
                              @keydown.esc="cancelQuestionOption"
                              style="width: 150px"
                            ></v-text-field>
                          </ValidationProvider>
                        </td>
                        <td class="text-no-wrap px-4 py-4 text-center">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                outlined
                                @click.stop="confirmQuestionOption"
                                color="green darken-2"
                                :disabled="invalid"
                                class="mr-2"
                                large
                              >
                                <v-icon small>$check</v-icon>
                              </v-btn>
                            </template>
                            <span>Confirm</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                color="red darken-2"
                                v-bind="attrs"
                                v-on="on"
                                icon
                                outlined
                                large
                                @click.stop="cancelQuestionOption"
                              >
                                <v-icon small>$close</v-icon>
                              </v-btn>
                            </template>
                            <span>Cancel</span>
                          </v-tooltip>
                        </td>
                      </ValidationObserver>
                      <tr
                        :key="`delete_${index}`"
                        v-if="deletingOption === index && !locked"
                      >
                        <td :colspan="optionGoals ? 4 : 3" class="red lighten-4 red--text py-3">
                          <div class="d-flex align-center">
                            <div class="flex-fill">
                              You are about to delete
                              <strong>{{ option.text }}</strong
                              >. Are you sure?
                            </div>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  v-bind="attrs"
                                  v-on="on"
                                  @click.stop="confirmDeleteQuestionOption"
                                  color="red darken-2 white--text"
                                  class="mr-2"
                                >
                                  Yes, Delete
                                </v-btn>
                              </template>
                              <span>Confirm</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  v-bind="attrs"
                                  v-on="on"
                                  @click.stop="cancelDeleteQuestionOption"
                                >
                                  Cancel
                                </v-btn>
                              </template>
                              <span>Cancel</span>
                            </v-tooltip>
                          </div>
                        </td>
                      </tr>
                    </template>
                    <ValidationObserver
                      v-if="
                        !locked &&
                          (addingOption ||
                            (innerValue.options &&
                              innerValue.options.length === 0))
                      "
                      v-slot="{ invalid }"
                      tag="tr"
                      ref="addOptionObserver"
                    >
                      <td class="px-4 py-4" style="vertical-align: top">
                        <ValidationProvider
                          rules="required|min:2|max:2000"
                          v-slot="{ errors }"
                          vid="addQuestionOptionText"
                        >
                          <v-text-field
                            label="Text*"
                            v-model="addOption.text"
                            outlined
                            :error-messages="errors"
                            :hide-details="errors.length == 0"
                            @keydown.enter.prevent="confirmAddQuestionOption"
                            :autofocus="!isNew"
                          ></v-text-field>
                        </ValidationProvider>
                      </td>
                      <td class="px-4 py-4" style="vertical-align: top">
                        <ValidationProvider
                          rules="required|max:100"
                          v-slot="{ errors }"
                          vid="addQuestionOptionValue"
                        >
                          <v-text-field
                            label="Value*"
                            v-model="addOption.value"
                            outlined
                            :error-messages="errors"
                            :hide-details="errors.length == 0"
                            @keydown.enter.prevent="confirmAddQuestionOption"
                            style="width: 150px"
                          ></v-text-field>
                        </ValidationProvider>
                      </td>
                      <td
                        v-if="optionGoals"
                        class="px-4 py-4"
                        style="vertical-align: top"
                      >
                        <ValidationProvider
                          rules="required"
                          v-slot="{ errors }"
                          vid="addQuestionOptionResponseCountTarget"
                        >
                          <v-text-field
                            type="number"
                            label="Response Goal*"
                            v-model="addOption.responseCountTarget"
                            outlined
                            :error-messages="errors"
                            :hide-details="errors.length == 0"
                            @keydown.enter.prevent="confirmAddQuestionOption"
                            style="width: 150px"
                          ></v-text-field>
                        </ValidationProvider>
                      </td>
                      <td class="text-no-wrap px-4 py-4 text-center">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              icon
                              outlined
                              @click.stop="confirmAddQuestionOption"
                              color="green darken-2"
                              :disabled="invalid"
                              class="mr-2"
                              large
                            >
                              <v-icon small>$check</v-icon>
                            </v-btn>
                          </template>
                          <span>Confirm</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              color="red darken-2"
                              v-bind="attrs"
                              v-on="on"
                              icon
                              outlined
                              large
                              v-if="innerValue.options.length > 0"
                              @click.stop="cancelAddQuestionOption"
                            >
                              <v-icon small>$close</v-icon>
                            </v-btn>
                          </template>
                          <span>Cancel</span>
                        </v-tooltip>
                      </td>
                    </ValidationObserver>
                  </table>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-divider class="mb-3"></v-divider>
                  <div class="text-h6">Conditional Rules</div>
                  <p class="text-body-2">
                    Use the section below to create conditional rules for your
                    question.
                  </p>

                  <question-rules
                    v-for="rule in rules"
                    :key="rule.key"
                    :label="rule.label"
                    :ruleKey="rule.key"
                    :queryRules="queryRules"
                    :locked="locked"
                    v-model="innerValue.rules"
                    class="mb-4"
                  ></question-rules>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions class="px-4 py-0 elevation-3">
            <v-container>
              <div class="d-flex align-center">
                <small class="flex-fill">*indicates required field</small>
                <v-btn text color="primary" @click="onCancel">
                  Cancel
                </v-btn>
                <v-btn
                  v-if="!locked"
                  type="submit"
                  color="primary"
                  :disabled="addingOption || editingOption > -1"
                >
                  Ok
                </v-btn>
              </div>
              <div
                v-if="addingOption"
                class="text-caption red--text darken-2 d-flex align-center justify-end"
              >
                <v-icon x-small class="red--text darken-2 mr-1">$error</v-icon>
                You must finish adding your
                <strong class="mx-1"> Option </strong>
                before saving.
              </div>
              <div
                v-if="editingOption > -1"
                class="text-caption red--text darken-2 d-flex align-center justify-end"
              >
                <v-icon x-small class="red--text darken-2 mr-1">$error</v-icon>
                You must finish editing your
                <strong class="mx-1"> Option </strong>
                before saving.
              </div>
              <div
                v-if="innerValue.options.length < 2"
                class="text-caption red--text darken-2 d-flex align-center justify-end"
              >
                <v-icon x-small class="red--text darken-2 mr-1">$error</v-icon>
                You must have at least
                <strong class="mx-1"> 2 Options </strong>
                before saving.
              </div>
            </v-container>
          </v-card-actions>
        </v-card>
      </ValidationObserver>
    </v-dialog>
  </div>
</template>

<style scoped lang="scss">
.wrapper {
  position: relative;
  z-index: 0;

  .clickLayer {
    opacity: 0;
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.15);
    transition: opacity 300ms ease-in-out;
    cursor: pointer;

    &:hover {
      opacity: 1;
    }
  }
}
</style>

<script>
import Vue from "vue";
import DialogTitle from "./DialogTitle.vue";
import {
  questionTypeOptions,
  questionSettings,
  getQuestionSetting,
  ruleTypes,
  updateSetting,
} from "../constants/Survey";
import equal from "deep-is";
import SurveyEditorPageSelect from "./Survey.Editor.PageSelect.vue";
import QuestionRules from "./QuestionRules.vue";
import SurveyQuestionRankedChoice from "./Survey.Question.RankedChoice.vue";

const newOption = () => ({
  text: "",
  value: "",
  responseCountTarget: null,
});

export default {
  components: {
    DialogTitle,
    SurveyEditorPageSelect,
    QuestionRules,
    SurveyQuestionRankedChoice,
  },
  data: () => ({
    innerValue: {
      settings: [],
      options: [],
    },
    innerPageKey: "",
    openEditor: false,
    addingOption: false,
    addOption: newOption(),
    editingOption: -1,
    editOption: newOption(),
    deletingOption: -1,
    optionGoals: false,
  }),
  props: {
    preview: {
      type: Boolean,
      default: true,
    },
    value: {
      type: Object,
    },
    questionIndex: {
      type: Number,
      required: true,
    },
    pages: {
      type: Array,
      required: true,
    },
    pageKey: {
      type: String,
      required: true,
    },
    questionKeys: {
      type: Array,
      required: true,
    },
    questionMappingKeys: {
      type: Array,
      required: true,
    },
    queryRules: {
      type: Array,
      required: true,
    },
    locked: Boolean,
  },
  computed: {
    types() {
      return questionTypeOptions;
    },
    pageOptions() {
      return this.pages.map((x) => {
        return {
          text: x.displayName,
          value: x.key,
        };
      });
    },
    page() {
      return this.pages.find((x) => x.key === this.pageKey);
    },
    isNew() {
      return this.value.key === "";
    },
    rules() {
      let rules = [];

      if (!this.required)
        rules.push({
          label: "Required If",
          key: ruleTypes.requiredIf,
        });

      rules.push({
        label: "Optional If",
        key: ruleTypes.optionalIf,
      });

      rules.push({
        label: "Show If",
        key: ruleTypes.showIf,
      });

      rules.push({
        label: "Hide If",
        key: ruleTypes.hideIf,
      });

      return rules;
    },
    slots: {
      get() {
        return +getQuestionSetting(
          this.innerValue.settings,
          questionSettings.slotCount.key
        ).value;
      },
      set(val) {
        updateSetting(
          this.innerValue.settings,
          questionSettings.slotCount.key,
          Math.floor(val) + ""
        );
      },
    },
    required: {
      get() {
        return (
          getQuestionSetting(
            this.innerValue.settings,
            questionSettings.required.key
          ).value == "true"
        );
      },
      set(val) {
        updateSetting(
          this.innerValue.settings,
          questionSettings.required.key,
          val + ""
        );
      },
    },
    isPrivate: {
      get() {
        return (
          getQuestionSetting(
            this.innerValue.settings,
            questionSettings.private.key
          ).value == "true"
        );
      },
      set(val) {
        updateSetting(
          this.innerValue.settings,
          questionSettings.private.key,
          val + ""
        );
      },
    },
    hasOptionGoals() {
      return this.innerValue.options.some((x) => x.responseCountTarget);
    },
  },
  watch: {
    value(val) {
      if (!equal(val, this.innerValue)) this.applyDefaults();
    },
    pageKey(value) {
      if (value != this.innerPageKey)
        this.innerPageKey = JSON.parse(JSON.stringify(value));
    },
    openEditor(val) {
      if (val) {
        this.innerValue = JSON.parse(JSON.stringify(this.value));

        this.addingOption = false;
        this.addOption = newOption();
        this.editingOption = -1;
        this.editOption = newOption();
        this.deletingOption = -1;

        if (this.pageKey != this.innerPageKey)
          this.innerPageKey = JSON.parse(JSON.stringify(this.pageKey));

        if (this.pageKey != this.innerPageKey)
          this.innerPageKey = JSON.parse(JSON.stringify(this.pageKey));

        if (this.$refs.body) {
          setTimeout(() => (this.$refs.body.scrollTop = 0), 1);
        }
      }
    },
  },
  methods: {
    edit() {
      if (this.$refs.observer) this.$refs.observer.reset();
      this.openEditor = true;
    },
    onSubmit() {
      this.cancelAddQuestionOption();

      this.$nextTick(() => {
        this.$refs.observer.validate().then((isValid) => {
          if (isValid) {
            if (this.innerValue.responseCountTarget === "") {
              this.innerValue.responseCountTarget = null;
            }

            this.$emit("input", this.innerValue);
            if (this.pageKey != this.innerPageKey) {
              this.$emit("set-page", this.innerPageKey);
            }
            this.openEditor = false;
          }
        });
      });
    },
    editQuestionOption(option, index) {
      this.editOption = JSON.parse(JSON.stringify(option));
      this.editingOption = index;
    },
    cancelQuestionOption() {
      this.editOption = newOption();
      this.editingOption = -1;
    },
    confirmQuestionOption() {
      Vue.set(this.innerValue.options, this.editingOption, this.editOption);
      this.cancelQuestionOption();
    },
    addQuestionOption() {
      this.addOption = newOption();
      this.addingOption = true;
    },
    cancelAddQuestionOption() {
      this.addOption = newOption();
      this.addingOption = false;
    },
    confirmAddQuestionOption() {
      this.innerValue.options.push(this.addOption);
      this.cancelAddQuestionOption();
    },
    deleteQuestionOption(option, index) {
      this.deletingOption = index;
    },
    cancelDeleteQuestionOption() {
      this.deletingOption = -1;
    },
    confirmDeleteQuestionOption() {
      this.innerValue.options.splice(this.deletingOption, 1);
      this.cancelDeleteQuestionOption();
    },
    moveOption(index, increment) {
      let mirror = JSON.parse(JSON.stringify(this.innerValue));
      let old = mirror.options[index + increment];

      mirror.options[index + increment] = mirror.options[index];
      mirror.options[index] = old;

      this.innerValue = mirror;
    },
    applyDefaults() {
      if (this.value) {
        let mirror = JSON.parse(JSON.stringify(this.value));
        let changed = false;

        let defaultSettings = [
          {
            key: questionSettings.required.key,
            value: questionSettings.required.default,
          },
          {
            key: questionSettings.private.key,
            value: questionSettings.private.default,
          },
          {
            key: questionSettings.slotCount.key,
            value: questionSettings.slotCount.default,
          },
        ];

        if (!mirror.settings) {
          mirror.settings = [];
          changed = true;
        }

        for (let i = 0; i < defaultSettings.length; i++) {
          if (!mirror.settings.some((x) => x.key === defaultSettings[i].key)) {
            mirror.settings.push(defaultSettings[i]);
            changed = true;
          }
        }

        if (changed) this.$emit("input", mirror);
        this.innerValue = JSON.parse(JSON.stringify(mirror));
      }
    },
    onCancel() {
      if (this.isNew) this.$emit("force-delete");
      this.openEditor = false;
    },
  },
  mounted() {
    if (this.pageKey)
      this.innerPageKey = JSON.parse(JSON.stringify(this.pageKey));
    this.applyDefaults();
  },
};
</script>
